"use client"

import { useEffect, useMemo, useState } from "react"
import { Button } from "@ui/shad"
import { Loader2, X } from "lucide-react"

import { useVisualViewportBounds } from "@/hooks/use-visual-viewport-height"

import type { FormBridgeFrameProps } from "./use-form-bridge"

const NOTICE_DURATION_MS = 8_000
const VIEWPORT_FOCUS_SCRIPT = `<script data-shell-viewport-focus>
(function () {
  var isIOS = /iP(ad|hone|od)/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (!isIOS) return;

  var pendingFrame = null;

  window.addEventListener("resize", function () {
    if (pendingFrame !== null) window.cancelAnimationFrame(pendingFrame);
    pendingFrame = window.requestAnimationFrame(function () {
      pendingFrame = null;
      var active = document.activeElement;
      if (!active || active === document.body || typeof active.scrollIntoView !== "function") return;
      active.scrollIntoView({ block: "center", inline: "nearest", behavior: "auto" });
    });
  });
})();
</script>`

function withViewportFocusScript(html: string | undefined) {
  if (!html || html.includes("data-shell-viewport-focus")) return html
  const closingBody = html.search(/<\/body\s*>/i)
  if (closingBody === -1) return `${html}${VIEWPORT_FOCUS_SCRIPT}`
  return `${html.slice(0, closingBody)}${VIEWPORT_FOCUS_SCRIPT}${html.slice(closingBody)}`
}

interface FormFrameProps {
  title: string
  html: string | undefined
  isLoading: boolean
  error: Error | null
  onRetry: () => void
  frameProps?: FormBridgeFrameProps
  notice?: React.ReactNode
  footer?: React.ReactNode
  embedded?: boolean
}

export function FormFrame({
  title,
  html,
  isLoading,
  error,
  onRetry,
  frameProps,
  notice,
  footer,
  embedded = false,
}: FormFrameProps) {
  const [showNotice, setShowNotice] = useState(Boolean(notice))
  const viewport = useVisualViewportBounds()
  // Published form artifacts are immutable, so inject this shell compatibility
  // behavior at render time to cover existing forms without requiring republish.
  const frameHtml = useMemo(() => withViewportFocusScript(html), [html])

  useEffect(() => {
    setShowNotice(Boolean(notice))
    if (!notice) return

    const timeout = window.setTimeout(() => setShowNotice(false), NOTICE_DURATION_MS)
    return () => window.clearTimeout(timeout)
  }, [notice])

  return (
    // Exactly the *visible* viewport height (tracks Safari chrome + keyboard via
    // visualViewport): the banner takes its slice and the iframe flexes into the
    // remainder, so the form's own bottom action bar stays on screen. Fixing the
    // shell also removes the outer document's scroll range, preventing iOS Safari
    // from panning the page while the iframe scrolls its focused field into view.
    <div
      className={embedded ? "bg-muted/30 flex h-full min-h-0 flex-col" : "bg-muted/30 fixed inset-x-0 flex flex-col"}
      style={embedded ? undefined : { height: viewport.height, top: viewport.offsetTop }}
    >
      {notice && showNotice && (
        <div
          role="status"
          className="border-blue-border bg-blue-background text-blue-foreground relative border-b px-12 py-2 text-center text-sm"
        >
          {notice}
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            disableInteraction
            aria-label="Dismiss notice"
            className="text-blue-foreground hover:bg-blue-background-hover absolute top-1/2 right-2 -translate-y-1/2"
            onClick={() => setShowNotice(false)}
          >
            <X />
          </Button>
        </div>
      )}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="text-muted-foreground size-6 animate-spin" />
        </div>
      ) : error ? (
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="max-w-sm text-center">
            <h1 className="font-semibold">Could not load the form</h1>
            <p className="text-muted-foreground mt-2 text-sm">{error.message}</p>
            <Button className="mt-4" variant="outline" onClick={onRetry}>
              Try again
            </Button>
          </div>
        </div>
      ) : (
        <iframe
          {...(frameProps ?? {})}
          title={title}
          srcDoc={frameHtml}
          sandbox="allow-forms allow-scripts allow-popups allow-popups-to-escape-sandbox"
          className="min-h-0 w-full flex-1 border-0"
        />
      )}
      {footer}
    </div>
  )
}
